import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import ChatbotPanel from './ChatbotPanel';
import { useChatbotAccess, CHATBOT_OPEN_EVENT } from '../hooks/useChatbotAccess';
import './chatbot-fab.css';

/** Modern "AI chat" glyph — a chat bubble with an AI sparkle. */
function AiChatIcon({ size = 24 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            aria-hidden="true">
            {/* speech bubble */}
            <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
            {/* AI sparkle inside the bubble */}
            <path d="M11 6.7l1.05 2.7 2.7 1.05-2.7 1.05L11 14.2l-1.05-2.7L7.25 10.45l2.7-1.05z"
                fill="currentColor" stroke="none" />
            {/* small twinkle */}
            <circle cx="15.5" cy="7.1" r="0.85" fill="currentColor" stroke="none" />
        </svg>
    );
}

/**
 * Chatbot Launcher — Sprint 2 F3.
 *
 * Global FAB + Ctrl+K shortcut. Mount once at app shell level.
 */
export default function ChatbotLauncher() {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    // Role + route + per-concessionnaire entitlement, all resolved by the shared
    // hook so the sidebar "Ask anything…" bar gets the identical verdict.
    // null = probe still in flight; false = hidden; true = show.
    const enabled = useChatbotAccess();

    useEffect(() => {
        if (!enabled) {
            // Gated off: make sure a panel opened before the verdict flipped
            // (e.g. an admin disabling the feature mid-session) doesn't linger.
            setOpen(false);
            return undefined;
        }
        function onKey(e) {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setOpen(o => !o);
            }
            if (e.key === 'Escape') setOpen(false);
        }
        function onOpenRequest() { setOpen(o => !o); }
        window.addEventListener('keydown', onKey);
        window.addEventListener(CHATBOT_OPEN_EVENT, onOpenRequest);
        return () => {
            window.removeEventListener('keydown', onKey);
            window.removeEventListener(CHATBOT_OPEN_EVENT, onOpenRequest);
        };
    }, [enabled]);

    // Hidden while the check is in flight, and whenever this user/route/org does
    // not have the AI chatbot.
    if (!enabled) return null;

    return (
        <>
            <button
                className={`v3-chatbot-fab ${open ? 'v3-chatbot-fab--open' : ''}`}
                onClick={() => setOpen(o => !o)}
                title={t('Ask AI (Ctrl+K)')}
                aria-label={open ? t('Close AI chatbot') : t('Open AI chatbot')}
            >
                <span className="v3-chatbot-fab__icon v3-chatbot-fab__icon--chat">
                    <AiChatIcon size={24} />
                </span>
                <span className="v3-chatbot-fab__icon v3-chatbot-fab__icon--close">
                    <X size={22} strokeWidth={2.5} />
                </span>
                <span className="v3-chatbot-fab__pulse" aria-hidden="true" />
            </button>
            <ChatbotPanel isOpen={open} onClose={() => setOpen(false)} />
        </>
    );
}
